import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import { TrackingEvent } from 'webapp/enums/tracking';

import ClipboardCopyComponent from 'webapp/components/clipboard-copy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type InfolayerItem from 'webapp/models-custom/infolayer-item/report-pdf';

interface Args {
	model: InfolayerItem;
	onClose: () => void;
}

export default class InfolayerTypeReportPdfComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;

	@action
	download(): void {
		const reportDelivery = this.args.model.reportDelivery;

		if (reportDelivery.reportId) {
			this.mmActivityLogsService.createAndSaveActivityLogItem(
				ActivityLogEvent.REPORT_DOWNLOADED,
				String(reportDelivery.reportId),
			);
		}

		this.analyticsService.event(TrackingEvent.REPORT_PDF_DOWNLOAD);

		const reportUrl = reportDelivery?.reportUrl;
		if (reportUrl) {
			window.open(reportUrl);
			this.args.onClose();
		}
	}

	@action
	onAfterCopyToClipboard(): void {
		this.analyticsService.event(TrackingEvent.REPORT_PDF_URL_COPY_PASTE);
		this.infolayerService.ok('PDF URL copied to clipboard!');
		this.args.onClose();
	}
	<template>
		<div role="button" class="infolayer infolayer--type-report-pdf {{@model.classes}}">
			<div>
				<CommonUiIconComponent @name="Pdf" @size="lg" @class="infolayer-icon" />
				{{#if @model.isLoading}}
					<p>
						Your report
						<strong>"{{@model.reportDelivery.title}}"</strong>
						(PDF) is being prepared. It will be available in your report archive
						shortly.
					</p>
				{{else if @model.isError}}
					<p>
						We've encountered the following error while generating your PDF report
						<strong>"{{@model.reportDelivery.title}}"</strong>:
						{{@model.errorText}}
					</p>
				{{else}}
					<div>
						<span class="title">{{@model.reportDelivery.title}}</span>
						<p>{{@model.reportDelivery.sizeFormatted}} - PDF document file</p>
					</div>
					<div class="controls2">
						<div class="controls2__right">
							<a class="close" {{on "click" @onClose}}>Dismiss</a>
							{{#if @model.reportDelivery.reportUrl}}
								<a class="button theme-grey">
									<CommonUiIconComponent @name="Copy" @size="sm" />Copy URL
									<ClipboardCopyComponent
										@text={{@model.reportDelivery.reportUrl}}
										@onAfterCopy={{this.onAfterCopyToClipboard}}
									/>
								</a>
							{{/if}}
							<a class="button" {{on "click" this.download}}>
								<CommonUiIconComponent @name="Download" @size="sm" />Download
							</a>
						</div>
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
